import React, { useEffect, useRef, useState } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import axios from 'axios';
import {
  cytoscapeStyles,
  cytoscapeLayout,
  cytoscapeSubgraphLayout,
  nodeTypeColors,
  edgeTypeColors,
  nodeTypeLabels,
} from '../utils/cytoscapeConfig';

const humanizeEdgeType = (type) =>
type
.toLowerCase()
.split('_')
.map(w => w.charAt(0).toUpperCase() + w.slice(1))
.join(' ');


const KnowledgeGraphPage = () => {
  const cyRef = useRef(null);
  const [graph, setGraph] = useState(null);
  const fullGraphRef = useRef(null);  // Store original full graph for resetting
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedSubgraph, setSelectedSubgraph] = useState(null);  // Store subgraph data
  const [filteredNodeType, setFilteredNodeType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');  // Search query state
  const [searchResults, setSearchResults] = useState([]);  // Search dropdown results
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);  // Show/hide dropdown
  const searchDebounceRef = useRef(null);  // Debounce timer ref
  const navigationHistoryRef = useRef([]);  // Browser-like history stack
  const currentHistoryIndexRef = useRef(-1);  // Current position in history

  // Use relative path /api which Traefik routes to backend
  // In Docker with Traefik, this resolves to the backend service
  // In local dev, would need to be configured differently
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

  /**
   * Fetch graph data from backend
   */
  const fetchGraphData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch graph metadata
      const metaResponse = await axios.get(`${API_BASE_URL}/kg/metadata`);
      setMetadata(metaResponse.data);

      // Fetch full graph
      const graphResponse = await axios.get(`${API_BASE_URL}/kg`);
      const { nodes, edges } = graphResponse.data;

      // Transform nodes and edges for Cytoscape
      // First pass: count edges per node
      const edgeCount = {};
      edges.forEach(edge => {
        edgeCount[edge.srcId] = (edgeCount[edge.srcId] || 0) + 1;
        edgeCount[edge.dstId] = (edgeCount[edge.dstId] || 0) + 1;
      });
      
      // Find max edges for scaling
      const maxEdges = Math.max(...Object.values(edgeCount), 1);
      
      const cytoscapeNodes = nodes.map((node, index) => {
        const degree = edgeCount[node.id] || 0;
        // Scale node size based on degree: min 10px, max 30px
        const scaledSize = 10 + (degree / maxEdges) * 20;
        
        const nodeData = {
          id: node.id,
          label: node.label,
          type: node.type,
          nodeType: node.type,  // Add explicit nodeType field for styling
          degree: degree,  // Store degree for styling
          ...node.props,
          weight: degree || 1,  // Weight for layout calculations
        };
        
        // Add classes for Cytoscape styling
        const classes = [node.type];
        
        return {
          data: nodeData,
          classes: classes,
          // Store size info for later style application
          scratchPad: {
            scaledSize: scaledSize,
          },
        };
      });

      const cytoscapeEdges = edges.map(edge => ({
        data: {
          id: edge.id,
          source: edge.srcId,
          target: edge.dstId,
          type: edge.type,
          ...edge.props,
        },
      }));

      setGraph({
        nodes: cytoscapeNodes,
        edges: cytoscapeEdges,
      });
      
      // Store full graph for resetting later
      fullGraphRef.current = {
        nodes: cytoscapeNodes,
        edges: cytoscapeEdges,
      };
    } catch (err) {
      console.error('Error fetching graph data:', err);
      setError(
        err.response?.data?.error ||
        err.message ||
        'Failed to fetch graph data'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initialize graph on component mount
   */
  useEffect(() => {
    fetchGraphData();
  }, []);

  /**
   * Handle node click - fetch details and neighbors
   */
  const handleNodeClick = async (event) => {
    const nodeFromGraph = event.target.data();
    
    // Save to navigation history before navigating to new node
    // This creates a browser-like history stack
    const targetId = nodeFromGraph.id;
    const targetLabel = nodeFromGraph.label || nodeFromGraph.id;
    const targetType = nodeFromGraph.type || '';

    if (navigationHistoryRef.current.length === 0) {
      // First node click from the full graph
      navigationHistoryRef.current.push({ id: targetId, label: targetLabel, type: targetType });
      currentHistoryIndexRef.current = 0;
    } else {
      // If we clicked a new node, push it to history, removing anything "forward"
      navigationHistoryRef.current = navigationHistoryRef.current.slice(0, currentHistoryIndexRef.current + 1);
      // Don't push if we're clicking the same active node again
      if (navigationHistoryRef.current[currentHistoryIndexRef.current].id !== targetId) {
          navigationHistoryRef.current.push({ id: targetId, label: targetLabel, type: targetType });
          currentHistoryIndexRef.current = navigationHistoryRef.current.length - 1;
      }
    }
    
    await loadNodeProfile(nodeFromGraph);
  };

  /**
   * Load a specific node's details and subgraph
   */
  const loadNodeProfile = async (nodeFromGraph) => {
    try {
      // Step 1: Fetch detailed node information
      const nodeDetailsResponse = await axios.get(`${API_BASE_URL}/kg/node/${nodeFromGraph.id}`);
      let nodeDetails = nodeDetailsResponse.data.data || nodeDetailsResponse.data;
      
      // If node details don't have label and type, use data from the graph node
      if (!nodeDetails.label) {
        nodeDetails.label = nodeFromGraph.label || nodeFromGraph.id;
      }
      if (!nodeDetails.type) {
        nodeDetails.type = nodeFromGraph.type;
      }
      if (!nodeDetails.id) {
        nodeDetails.id = nodeFromGraph.id;
      }
      
      // Step 2: Fetch immediate neighbors (subgraph)
      const neighborsResponse = await axios.get(`${API_BASE_URL}/kg/node/${nodeFromGraph.id}/neighbors`);
      const subgraph = neighborsResponse.data;
      
      console.log('Node From Graph:', nodeFromGraph);
      console.log('Node Details from API:', nodeDetailsResponse.data);
      console.log('Neighbors Subgraph:', subgraph);
      
      // Set selected node with full details - ensuring label and type are present
      // Flatten props if they exist (from backend Node structure)
      const flattenedDetails = {
        id: nodeDetails.id,
        label: nodeDetails.label,
        type: nodeDetails.type,
        ...nodeDetails,
        ...(nodeDetails.props || {}),  // Flatten props object
        degree: subgraph.stats.connectionCount,
      };
      
      setSelectedNode(flattenedDetails);
      
      // Store subgraph for connection display
      setSelectedSubgraph(subgraph);
      
      // Update graph to show only this subgraph
      if (cyRef.current) {
        // Transform subgraph nodes to Cytoscape format
        const sgNodes = subgraph.nodes.map(n => {
          const degree = subgraph.edges.filter(e => e.srcId === n.id || e.dstId === n.id).length;
          return {
            data: {
              id: n.id,
              label: n.label,
              type: n.type,
              nodeType: n.type,
              degree: degree,
              ...n.props,
              weight: degree || 1,
            },
            classes: [n.type, 'show-label'],  // Add show-label class to display labels
          };
        });
        
        // Transform subgraph edges to Cytoscape format
        const sgEdges = subgraph.edges.map(edge => ({
          data: {
            id: edge.id,
            source: edge.srcId,
            target: edge.dstId,
            type: edge.type,
            ...edge.props,
          },
        }));
        
        // Clear current graph and add subgraph
        cyRef.current.elements().remove();
        cyRef.current.add([...sgNodes, ...sgEdges]);
        
        // Run layout on the new subgraph using subgraph-optimized layout
        const layout = cyRef.current.layout(cytoscapeSubgraphLayout);
        layout.run();
        
        // Wait for layout to finish, then fit to new subgraph
        setTimeout(() => {
          cyRef.current.fit(null, 50);
        }, 500);
      }
    } catch (err) {
      console.error('Error fetching node details or neighbors:', err);
      // Fallback to basic node data
      setSelectedNode(nodeFromGraph);
    }
  };

  /**
   * Handle node type filter
   */
  const handleNodeTypeFilter = (type) => {
    setFilteredNodeType(type);
    if (cyRef.current) {
      if (type === '') {
        cyRef.current.elements().show();
      } else {
        // Hide nodes not of selected type
        cyRef.current.nodes().forEach(node => {
          if (node.data('type') === type) {
            node.show();
            node.connectedEdges().show();
          } else {
            node.hide();
            node.connectedEdges().hide();
          }
        });
      }
    }
  };

  /**
   * Reset view to show all nodes
   */
  const handleResetView = () => {
    setFilteredNodeType('');
    setSelectedNode(null);
    setSelectedSubgraph(null);
    // Don't clear history immediately, but treat reset as returning to state -1.
    // However, if we forcefully reset, typically we might wipe history (or keep it if you want forward to work). 
    // We will clear it to avoid confusion when starting a new exploration from scratch via Reset button:
    navigationHistoryRef.current = []; 
    currentHistoryIndexRef.current = -1; // Reset index
    
    if (cyRef.current && fullGraphRef.current) {
      // Reload full graph from stored reference
      cyRef.current.elements().remove();
      cyRef.current.add([...fullGraphRef.current.nodes, ...fullGraphRef.current.edges]);
      cyRef.current.fit(null, 50);
    }
  };

  /**
   * Go back to previous node in navigation history (like browser back button)
   */
  const handleBackNavigation = () => {
    if (currentHistoryIndexRef.current > 0) {
      currentHistoryIndexRef.current--;
      const previousNode = navigationHistoryRef.current[currentHistoryIndexRef.current];
      
      // Load the previous node profile without messing with the history stack
      loadNodeProfile(previousNode);
    } else if (currentHistoryIndexRef.current === 0) {
      // Go back to the full graph view
      currentHistoryIndexRef.current--;
      setFilteredNodeType('');
      setSelectedNode(null);
      setSelectedSubgraph(null);
      if (cyRef.current && fullGraphRef.current) {
        cyRef.current.elements().remove();
        cyRef.current.add([...fullGraphRef.current.nodes, ...fullGraphRef.current.edges]);
        
        // Re-run the layout on the full graph
        const layout = cyRef.current.layout(cytoscapeLayout);
        layout.run();
        
        // Wait for layout to finish, then fit to view
        setTimeout(() => {
          cyRef.current.fit(null, 50);
        }, 500);
      }
    }
  };

  /**
   * Go forward in navigation history (like browser forward button)
   */
  const handleForwardNavigation = () => {
    if (currentHistoryIndexRef.current < navigationHistoryRef.current.length - 1) {
      currentHistoryIndexRef.current++;
      const nextNode = navigationHistoryRef.current[currentHistoryIndexRef.current];
      
      // Load the next node profile without messing with the history stack
      loadNodeProfile(nextNode);
    }
  };

  /**
   * Format connection labels based on node type and edge type
   */
  const getConnectionLabel = (nodeType, edgeType, isIncoming) => {
    const labels = {
      Planner: {
        CONTRIBUTED_TO: isIncoming ? 'Contributors' : 'Contributed to',
        SUPPORTS_PLANNING_CLASS: 'Supports planning classes',
        PROVIDES_PLANNING_FUNCTIONALITY: 'Provides planning types',
        SUPPORTS_PLANNING_LANGUAGE: 'Supports planning languages',
        IMPLEMENTED_IN: 'Implementation language',
        PUBLISHED_IN: isIncoming ? 'Planners (by year)' : 'Planner was published in',
      },
      Person: {
        CONTRIBUTED_TO: 'Contributed to planners',
      },
      PlanningClass: {
        SUPPORTS_PLANNING_CLASS: 'Supported by planners',
      },
      PlanningType: {
        PROVIDES_PLANNING_FUNCTIONALITY: 'Tools providing this functionality',
      },
      PlanningLanguage: {
        SUPPORTS_PLANNING_LANGUAGE: 'Supported by planners',
      },
      ImplementationLanguage: {
        IMPLEMENTED_IN: 'Used by planners',
      },
      Year: {
        PUBLISHED_IN: 'Planners', // or 'Planners published in' if viewing a Year node
      },
    };
    
    return labels[nodeType]?.[edgeType] || edgeType;
  };

  /**
   * Organize neighbors by connection type and direction
   */
  const organizeConnections = (subgraph, centralNodeId, centralNodeType) => {
    const connections = {};
    
    subgraph.edges.forEach(edge => {
      const isOutgoing = edge.srcId === centralNodeId;
      const connectedNodeId = isOutgoing ? edge.dstId : edge.srcId;
      const connectedNode = subgraph.nodes.find(n => n.id === connectedNodeId);
      
      if (!connectedNode || connectedNode.id === centralNodeId) return;
      
      const label = getConnectionLabel(centralNodeType, edge.type, !isOutgoing);
      
      if (!connections[label]) {
        connections[label] = [];
      }
      
      connections[label].push(connectedNode);
    });
    
    return connections;
  };

  /**
   * Perform debounced search for nodes
   */
  const performSearch = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/kg/search`, {
        params: {
          query: query,
          limit: 5,
        },
      });
      
      setSearchResults(response.data.data || response.data || []);
      setShowSearchDropdown(true);
    } catch (err) {
      console.error('Error searching nodes:', err);
      setSearchResults([]);
      setShowSearchDropdown(false);
    }
  };

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new debounce timer (300ms delay)
    searchDebounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  /**
   * Handle selecting a node from search dropdown
   */
  const handleSearchResultSelect = async (node) => {
    try {
      // Save to navigation history before navigating to new node
      // This creates a browser-like history stack for search selections
      const targetId = node.id;
      const targetLabel = node.label || node.id;
      const targetType = node.type || '';

      // Remove any forward history when selecting from search
      navigationHistoryRef.current = navigationHistoryRef.current.slice(0, currentHistoryIndexRef.current + 1);
      // Push the new node to history
      navigationHistoryRef.current.push({ id: targetId, label: targetLabel, type: targetType });
      currentHistoryIndexRef.current = navigationHistoryRef.current.length - 1;

      // Step 1: Fetch detailed node information
      const nodeDetailsResponse = await axios.get(`${API_BASE_URL}/kg/node/${node.id}`);
      let nodeDetails = nodeDetailsResponse.data.data || nodeDetailsResponse.data;
      
      // If node details don't have label and type, use data from the search result
      if (!nodeDetails.label) {
        nodeDetails.label = node.label || node.id;
      }
      if (!nodeDetails.type) {
        nodeDetails.type = node.type;
      }
      if (!nodeDetails.id) {
        nodeDetails.id = node.id;
      }
      
      // Step 2: Fetch immediate neighbors (subgraph)
      const neighborsResponse = await axios.get(`${API_BASE_URL}/kg/node/${node.id}/neighbors`);
      const subgraph = neighborsResponse.data;
      
      // Set selected node with full details
      // Flatten props if they exist (from backend Node structure)
      const flattenedDetails = {
        id: nodeDetails.id,
        label: nodeDetails.label,
        type: nodeDetails.type,
        ...nodeDetails,
        ...(nodeDetails.props || {}),  // Flatten props object
        degree: subgraph.stats.connectionCount,
      };
      
      setSelectedNode(flattenedDetails);
      
      // Store subgraph for connection display
      setSelectedSubgraph(subgraph);
      
      // Update graph to show only this subgraph
      if (cyRef.current) {
        // Transform subgraph nodes to Cytoscape format
        const sgNodes = subgraph.nodes.map(n => {
          const degree = subgraph.edges.filter(e => e.srcId === n.id || e.dstId === n.id).length;
          return {
            data: {
              id: n.id,
              label: n.label,
              type: n.type,
              nodeType: n.type,
              degree: degree,
              ...n.props,
              weight: degree || 1,
            },
            classes: [n.type, 'show-label'],
          };
        });
        
        // Transform subgraph edges to Cytoscape format
        const sgEdges = subgraph.edges.map(edge => ({
          data: {
            id: edge.id,
            source: edge.srcId,
            target: edge.dstId,
            type: edge.type,
            ...edge.props,
          },
        }));
        
        // Clear current graph and add subgraph
        cyRef.current.elements().remove();
        cyRef.current.add([...sgNodes, ...sgEdges]);
        
        // Run layout on the new subgraph using subgraph-optimized layout
        const layout = cyRef.current.layout(cytoscapeSubgraphLayout);
        layout.run();
        
        // Wait for layout to finish, then fit to new subgraph
        setTimeout(() => {
          cyRef.current.fit(null, 50);
        }, 500);
      }
      
      // Close dropdown and clear search
      setShowSearchDropdown(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      console.error('Error selecting search result:', err);
    }
  };

  const nodeTypes = Object.keys(nodeTypeColors);
  const edgeTypes = Object.keys(edgeTypeColors);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50 py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <p className="mt-4 text-lg font-semibold text-gray-700">Loading Knowledge Graph...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50 py-12 px-4">
        <div className="w-full">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Graph</h2>
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchGraphData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!graph) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50 py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-700">No graph data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50 flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 bg-white/80">
        <p className="text-gray-600 text-sm">
          Interactive visualization of planning tools and their relationships
        </p>
      </div>

      {/* Main content: header + center + footer (legend) */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Center area: sidebar + graph + details */}
        <div className="flex-1 min-h-0 overflow-hidden w-full px-6 pt-6">
          <div className="grid grid-cols-12 gap-6 h-full w-full min-h-0">
          {/* Sidebar - Reduced width */}
          <div className="col-span-2 bg-white rounded-lg shadow-lg p-4 overflow-y-auto h-full">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Controls</h2>

            {/* Search Bar */}
            <div className="mb-4 pb-4 border-b border-gray-200 relative">
              <input
                type="text"
                placeholder="Search nodes"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-teal-600"
              />
              
              {/* Search Dropdown */}
              {showSearchDropdown && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 max-h-56 overflow-y-auto">
                  {searchResults.map((result) => (
                    <div
                      key={result.id}
                      onClick={() => handleSearchResultSelect(result)}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-200 last:border-b-0 flex items-center gap-2 transition"
                    >
                      <span
                        className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: nodeTypeColors[result.type] || '#999' }}
                      ></span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">
                          {result.label || result.id}
                          {result.shortName && result.type === 'Planner' && (
                            <span className="text-gray-500"> ({result.shortName})</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">{nodeTypeLabels[result.type] || result.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Graph Statistics */}
            {metadata && (
              <div className="mb-4 pb-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-2 text-sm">Statistics</h3>
                <p className="text-xs text-gray-600">
                  Nodes: <span className="font-bold">{metadata.nodeCount}</span>
                </p>
                <p className="text-xs text-gray-600">
                  Edges: <span className="font-bold">{metadata.edgeCount}</span>
                </p>
                {metadata.nodesByType && (
                  <div className="mt-2 text-xs text-gray-600">
                    <p className="font-semibold mb-1">By Node Type:</p>
                    {Object.entries(metadata.nodesByType).map(([type, count]) => (
                      <p key={type} className="text-xs">{nodeTypeLabels[type] || type}: {count}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Layout Options */}

            {/* Node Type Filter */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-2 text-sm">Filter by Node Type</h3>
              <button
                onClick={() => handleNodeTypeFilter('')}
                className={`w-full px-2 py-1 rounded text-xs font-medium transition mb-2 ${
                  filteredNodeType === ''
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All Types
              </button>
              {nodeTypes.map(type => (
                <button
                  key={type}
                  onClick={() => handleNodeTypeFilter(type)}
                  className={`w-full px-2 py-1 rounded text-xs font-medium transition block mb-1 ${
                    filteredNodeType === type
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  style={
                    filteredNodeType === type
                      ? {}
                      : { borderLeft: `3px solid ${nodeTypeColors[type]}` }
                  }
                >
                  {nodeTypeLabels[type] || type}
                </button>
              ))}
            </div>
          </div>

          {/* Main Graph Area */}
          <div className="col-span-7 bg-white rounded-lg shadow-lg overflow-hidden flex flex-col relative h-full">
            {/* Zoom Controls */}
            <div className="absolute top-4 right-4 z-40 flex flex-col gap-2 bg-white rounded-lg shadow-md border border-gray-200">
              <button
                onClick={handleBackNavigation}
                disabled={currentHistoryIndexRef.current <= -1}
                className="p-2 hover:bg-gray-100 transition rounded-t-lg border-b border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Go back to previous node"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>

              <button
                onClick={handleForwardNavigation}
                disabled={currentHistoryIndexRef.current >= navigationHistoryRef.current.length - 1}
                className="p-2 hover:bg-gray-100 transition border-b border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Go forward to next node"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>

              <button
                onClick={() => {
                  if (cyRef.current) {
                    const currentZoom = cyRef.current.zoom();
                    cyRef.current.zoom(currentZoom + 0.3);
                  }
                }}
                className="p-2 hover:bg-gray-100 transition border-b border-gray-200"
                title="Zoom In"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              
              <button
                onClick={() => {
                  if (cyRef.current) {
                    const currentZoom = cyRef.current.zoom();
                    cyRef.current.zoom(Math.max(currentZoom - 0.3, 0.1));
                  }
                }}
                className="p-2 hover:bg-gray-100 transition border-b border-gray-200"
                title="Zoom Out"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              
              <button
                onClick={() => {
                  if (cyRef.current && fullGraphRef.current) {
                    cyRef.current.fit(null, 50);
                  }
                }}
                className="p-2 hover:bg-gray-100 transition rounded-b-lg"
                title="Fit to View"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 1v4m0 0h-4m4 0l-5-5" />
                </svg>
              </button>
            </div>

            <CytoscapeComponent
              elements={[...graph.nodes, ...graph.edges]}
              style={{
                width: '100%',
                height: '100%',
                flex: 1,
                display: 'flex',
              }}
              stylesheet={cytoscapeStyles}
              layout={cytoscapeLayout}
              cy={cy => {
                cyRef.current = cy;
                cy.off('tap', 'node'); // Remove existing listeners to prevent multiple API calls
                cy.on('tap', 'node', handleNodeClick);
                
                // Fit to view when graph initially loads
                setTimeout(() => {
                  cy.fit(null, 50);
                }, 500);
                
                // Debug: Log first 5 nodes and their classes
                const nodes = cy.nodes();
                console.log('Total nodes in Cytoscape:', nodes.length);
                for (let i = 0; i < Math.min(5, nodes.length); i++) {
                  const node = nodes[i];
                  console.log(`Cytoscape Node ${i}:`, {
                    id: node.id(),
                    data: node.data(),
                    classes: node.classes(),
                    style: node.style('background-color'),
                  });
                }
              }}
            />
          </div>

          {/* Right Panel - Node Details */}
          <div className="col-span-3 bg-white rounded-lg shadow-lg p-4 flex flex-col h-full" style={{ maxHeight: '100%' }}>
            {selectedNode ? (
              <>
                <div className="flex justify-between items-start mb-4 pb-4 border-b border-gray-200 flex-shrink-0">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 break-words">{selectedNode.label || 'Unknown'}</h2>
                    {selectedNode.shortName && selectedNode.type === 'Planner' && (
                      <p className="text-xs text-gray-500 mt-1">({selectedNode.shortName})</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="font-semibold text-xs text-gray-700">Type:</span>
                      <span
                        className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: nodeTypeColors[selectedNode.type] || '#999' }}
                      ></span>
                      <span className="text-xs text-gray-600">{selectedNode.type || 'Unknown'}</span>
                    </div>
                    
                    {/* Planner-specific properties */}
                    {selectedNode.type === 'Planner' && (
                      <div className="mt-3 space-y-2">
                        {selectedNode.year && (
                          <div>
                            <span className="font-semibold text-xs text-gray-700">Year:</span>
                            <p className="text-xs text-gray-600 mt-1">{selectedNode.year}</p>
                          </div>
                        )}
                        {selectedNode.abstract && (
                          <div>
                            <span className="font-semibold text-xs text-gray-700">Abstract:</span>
                            <p className="text-xs text-gray-600 mt-1 line-clamp-3">{selectedNode.abstract}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto min-h-0 pr-2">
                  {/* Connections organized by type */}
                  {selectedSubgraph && (
                    <div className="space-y-4">
                    {(() => {
                      const connections = organizeConnections(selectedSubgraph, selectedNode.id, selectedNode.type);
                      
                      if (Object.keys(connections).length === 0) {
                        return <p className="text-xs text-gray-500">No connections</p>;
                      }
                      
                      return Object.entries(connections).map(([connectionType, nodes]) => (
                        <div key={connectionType}>
                          <h4 className="font-semibold text-gray-700 mb-2 text-sm">{connectionType}</h4>
                          <ul className="space-y-1 ml-2">
                            {nodes.map(node => (
                              <li 
                                key={node.id} 
                                onClick={async () => {
                                  // Save current node to history before navigating
                                  // This creates a browser-like history stack for details panel clicks
                                  const targetId = node.id;
                                  const targetLabel = node.label || node.id;
                                  const targetType = node.type || '';

                                  // Remove any forward history when clicking a new node from details panel
                                  navigationHistoryRef.current = navigationHistoryRef.current.slice(0, currentHistoryIndexRef.current + 1);
                                  // Push the new node to history
                                  navigationHistoryRef.current.push({ id: targetId, label: targetLabel, type: targetType });
                                  currentHistoryIndexRef.current = navigationHistoryRef.current.length - 1;

                                  // Trigger node click handler for this connection node
                                  try {
                                    // Step 1: Fetch detailed node information
                                    const nodeDetailsResponse = await axios.get(`${API_BASE_URL}/kg/node/${node.id}`);
                                    let nodeDetails = nodeDetailsResponse.data.data || nodeDetailsResponse.data;
                                    
                                    // If node details don't have label and type, use data from the graph node
                                    if (!nodeDetails.label) {
                                      nodeDetails.label = node.label || node.id;
                                    }
                                    if (!nodeDetails.type) {
                                      nodeDetails.type = node.type;
                                    }
                                    if (!nodeDetails.id) {
                                      nodeDetails.id = node.id;
                                    }
                                    
                                    // Step 2: Fetch immediate neighbors (subgraph)
                                    const neighborsResponse = await axios.get(`${API_BASE_URL}/kg/node/${node.id}/neighbors`);
                                    const subgraph = neighborsResponse.data;
                                    
                                    console.log('Clicked connection node:', nodeDetails);
                                    console.log('Neighbors subgraph:', subgraph);
                                    
                                    // Set selected node with full details
                                    // Flatten props if they exist (from backend Node structure)
                                    const flattenedDetails = {
                                      id: nodeDetails.id,
                                      label: nodeDetails.label,
                                      type: nodeDetails.type,
                                      ...nodeDetails,
                                      ...(nodeDetails.props || {}),  // Flatten props object
                                      degree: subgraph.stats.connectionCount,
                                    };
                                    
                                    setSelectedNode(flattenedDetails);
                                    
                                    // Store subgraph for connection display
                                    setSelectedSubgraph(subgraph);
                                    
                                    // Update graph to show only this subgraph
                                    if (cyRef.current) {
                                      // Transform subgraph nodes to Cytoscape format
                                      const sgNodes = subgraph.nodes.map(n => {
                                        const degree = subgraph.edges.filter(e => e.srcId === n.id || e.dstId === n.id).length;
                                        return {
                                          data: {
                                            id: n.id,
                                            label: n.label,
                                            type: n.type,
                                            nodeType: n.type,
                                            degree: degree,
                                            ...n.props,
                                            weight: degree || 1,
                                          },
                                          classes: [n.type, 'show-label'],
                                        };
                                      });
                                      
                                      // Transform subgraph edges to Cytoscape format
                                      const sgEdges = subgraph.edges.map(edge => ({
                                        data: {
                                          id: edge.id,
                                          source: edge.srcId,
                                          target: edge.dstId,
                                          type: edge.type,
                                          ...edge.props,
                                        },
                                      }));
                                      
                                      // Clear current graph and add subgraph
                                      cyRef.current.elements().remove();
                                      cyRef.current.add([...sgNodes, ...sgEdges]);
                                      
                                      // Run layout on the new subgraph using subgraph-optimized layout
                                      const layout = cyRef.current.layout(cytoscapeSubgraphLayout);
                                      layout.run();
                                      
                                      // Wait for layout to finish, then fit to new subgraph
                                      setTimeout(() => {
                                        cyRef.current.fit(null, 50);
                                      }, 500);
                                    }
                                  } catch (err) {
                                    console.error('Error clicking connection node:', err);
                                  }
                                }}
                                className="text-xs text-gray-600 flex items-center cursor-pointer hover:text-teal-600 transition"
                              >
                                <span
                                  className="inline-block w-2 h-2 rounded-full mr-2 flex-shrink-0"
                                  style={{ backgroundColor: nodeTypeColors[node.type] }}
                                ></span>
                                <span className="break-words">{node.label || node.key}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ));
                    })()}
                  </div>
                )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <p className="text-gray-500 text-sm">Select a node to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Legend - Below Graph (footer) */}
      <div className="px-6 pt-4 pb-4 flex-none">
        <div className="bg-white rounded-lg shadow-lg p-4">
          <h2 className="text-base font-bold text-gray-900 mb-3">Legend</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2 text-xs">Node Types</h3>
              <div className="space-y-1">
                {Object.entries(nodeTypeColors).map(([type, color]) => (
                  <div key={type} className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: color }}
                    ></div>
                    <span className="text-xs text-gray-600">{nodeTypeLabels[type]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2 text-xs">Edge Types</h3>
              <div className="space-y-1">
                {Object.entries(edgeTypeColors).map(([type, color]) => (
                  <div key={type} className="flex items-center">
                    <div
                      className="h-1 w-3 mr-2 flex-shrink-0"
                      style={{ backgroundColor: color }}
                    ></div>
                    <span className="text-xs text-gray-600">{humanizeEdgeType(type)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2 text-xs">Interactions</h3>
              <div className="space-y-0.5 text-xs text-gray-600">
                <p>• <span className="font-medium">Click</span> node for details</p>
                <p>• <span className="font-medium">Drag</span> to move</p>
                <p>• <span className="font-medium">Scroll</span> to zoom</p>
                <p>• <span className="font-medium">Zoom buttons</span> for control</p>
                <p>• <span className="font-medium">Filter</span> by type</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraphPage;
